import type { ReactNode } from 'react';
import { isApiError } from '../api/client';
import { useAdminAccess, useSession, useSignOut } from '../api/hooks';
import { Button, ErrorNote, Loading } from '../ui';
import { Wordmark } from './Shell';
import { SignIn } from './SignIn';
import s from './Shell.module.css';

/**
 * Signed out → sign-in. Signed in → ask the API who this is; the server decides access from the
 * roles it re-reads on every request, so a 403 here means this account has no back-office role.
 */
export function AccessGate({ children }: { children: ReactNode }) {
  const session = useSession();
  if (!session) return <SignIn />;
  return <Probe>{children}</Probe>;
}

function Probe({ children }: { children: ReactNode }) {
  const access = useAdminAccess();
  if (access.isSuccess) return <>{children}</>;
  if (access.isPending) {
    return (
      <div className={s.center}>
        <Loading label="正在檢查後台權限…" />
      </div>
    );
  }
  if (isApiError(access.error) && access.error.isForbidden)
    return <NoAdminRole reason={access.error.message} onRetry={() => void access.refetch()} checking={access.isFetching} />;
  return (
    <Screen title="未能載入後台">
      <ErrorNote error={access.error} />
      <Button variant="primary" onClick={() => void access.refetch()} busy={access.isFetching}>
        再試一次
      </Button>
    </Screen>
  );
}

function NoAdminRole({ reason, onRetry, checking }: { reason: string; onRetry: () => void; checking: boolean }) {
  const session = useSession();
  const suspended = reason === 'account unavailable';
  return (
    <Screen title={suspended ? '帳戶無法使用' : '沒有後台權限'}>
      <p>
        你以 <strong>{session?.email}</strong> 登入，但{suspended ? '這個帳戶已被停用或正在刪除。' : '這個帳戶沒有任何後台角色。'}
      </p>
      {!suspended && (
        <p className="muted small">
          請 owner 在「團隊與權限」頁加入你的電郵，或在伺服器執行 <code>node dist/admin-cli.js grant 你的電郵 owner</code>。每次請求都會重新讀取角色，獲授權後不必重新登入。
        </p>
      )}
      {!suspended && (
        <Button variant="primary" onClick={onRetry} busy={checking}>
          再檢查一次
        </Button>
      )}
    </Screen>
  );
}

function Screen({ title, children }: { title: string; children: ReactNode }) {
  const signOut = useSignOut();
  return (
    <div className={s.center}>
      <div className={s.panel}>
        <div className={s.panelBrand}>
          <Wordmark className="" />
        </div>
        <h1>{title}</h1>
        {children}
        <Button onClick={() => signOut.mutate()} busy={signOut.isPending}>
          登出
        </Button>
      </div>
    </div>
  );
}
